/**
 * OFProgressDialog - Diálogo de progresso detalhado da OF
 * Extraído de GestaoOFs.js para melhor modularidade
 */
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { AlertTriangle, Users, Layers, Edit3 } from 'lucide-react';
import useOFHelpers from './useOFHelpers';

export default function OFProgressDialog({
  open,
  onOpenChange,
  progressoDetalhado,
  participantes,
  onEditRegistos,
}) {
  const { getStatusBadge, getGrupoCor } = useOFHelpers();

  const handleClose = (isOpen) => {
    onOpenChange(isOpen);
  };

  if (!progressoDetalhado) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="progress-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center justify-between">
            <span>Progresso - {progressoDetalhado?.of?.numero_of}</span>
            {progressoDetalhado?.ocorrencias?.length > 0 && (
              <a 
                href="/gestor/anomalias" 
                className="text-sm bg-red-100 text-red-600 px-3 py-1 rounded-full flex items-center gap-1 hover:bg-red-200 transition-colors"
              >
                <AlertTriangle className="h-4 w-4" />
                {progressoDetalhado.ocorrencias.filter(o => o.status === 'aberta').length} Anomalias
              </a>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Info da OF */}
          <div className="bg-stone-50 p-4 rounded-lg">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-xs text-stone-500">Artigo</p>
                <p className="font-semibold">{progressoDetalhado.of.artigo}</p>
              </div>
              <div>
                <p className="text-xs text-stone-500">Cliente</p>
                <p className="font-semibold">{progressoDetalhado.of.cliente}</p>
              </div>
              <div>
                <p className="text-xs text-stone-500">Lotes</p>
                <p className="font-semibold">{progressoDetalhado.of.lotes?.length || 0}</p>
              </div>
              <div>
                <p className="text-xs text-stone-500">Status</p>
                {getStatusBadge(progressoDetalhado.of.status)}
              </div>
            </div>
          </div>

          {/* Resumo de Tempo */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="p-4 text-center">
              <p className="text-xs text-stone-500">Progresso</p>
              <p className="text-3xl font-bold text-primary">{progressoDetalhado.resumo.progresso_por_quantidade}%</p>
              <p className="text-xs text-stone-400">{progressoDetalhado.of.quantidade_produzida}/{progressoDetalhado.of.quantidade_total} un.</p>
            </Card>
            <Card className="p-4 text-center bg-blue-50 border-blue-200">
              <p className="text-xs text-blue-600">Tempo Estimado</p>
              <p className="text-2xl font-bold text-blue-700">{progressoDetalhado.resumo.tempo_total_estimado_horas}h</p>
              <p className="text-xs text-blue-500">{Math.round(progressoDetalhado.resumo.tempo_total_estimado_segundos / 60)}min</p>
            </Card>
            <Card className="p-4 text-center bg-amber-50 border-amber-200">
              <p className="text-xs text-amber-600">Tempo Gasto</p>
              <p className="text-2xl font-bold text-amber-700">{progressoDetalhado.resumo.tempo_total_gasto_horas}h</p>
              <p className="text-xs text-amber-500">{Math.round(progressoDetalhado.resumo.tempo_total_gasto_segundos / 60)}min</p>
            </Card>
            <Card className="p-4 text-center bg-green-50 border-green-200">
              <p className="text-xs text-green-600">Tempo Restante</p>
              <p className="text-2xl font-bold text-green-700">{progressoDetalhado.resumo.tempo_restante_horas}h</p>
              <p className="text-xs text-green-500">{Math.round(progressoDetalhado.resumo.tempo_restante_segundos / 60)}min</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-xs text-stone-500">Operações</p>
              <p className="text-2xl font-bold">{progressoDetalhado.resumo.operacoes_concluidas}/{progressoDetalhado.resumo.total_operacoes}</p>
              <p className="text-xs text-stone-400">concluídas</p>
            </Card>
          </div>

          {/* Barra de Progresso Geral */}
          <div className="bg-stone-50 p-4 rounded-lg">
            <div className="flex justify-between text-sm mb-2">
              <span className="font-medium">Progresso Geral</span>
              <span className="font-bold">{progressoDetalhado.resumo.progresso_por_quantidade}%</span>
            </div>
            <div className="w-full h-6 bg-stone-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-primary to-primary/70 rounded-full transition-all flex items-center justify-end pr-2"
                style={{ width: `${Math.max(progressoDetalhado.resumo.progresso_por_quantidade, 5)}%` }}
              >
                {progressoDetalhado.resumo.progresso_por_quantidade > 10 && (
                  <span className="text-xs text-white font-bold">{progressoDetalhado.resumo.progresso_por_quantidade}%</span>
                )}
              </div>
            </div>
          </div>

          {/* Ocorrências/Anomalias */}
          {progressoDetalhado.ocorrencias?.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Ocorrências ({progressoDetalhado.ocorrencias.length})
                </h3>
                <a href="/gestor/anomalias" className="text-sm text-primary hover:underline">Ver Todas →</a>
              </div>
              <div className="space-y-2">
                {progressoDetalhado.ocorrencias.slice(0, 3).map((oc) => (
                  <div 
                    key={oc.id} 
                    className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg" 
                    style={{ borderLeft: `4px solid ${oc.tipo_cor}` }}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span 
                          className="text-xs font-medium px-2 py-0.5 rounded text-white" 
                          style={{ backgroundColor: oc.tipo_cor }}
                        >
                          {oc.tipo_nome}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          oc.status === 'aberta' ? 'bg-red-100 text-red-600' : 
                          oc.status === 'em_analise' ? 'bg-amber-100 text-amber-600' : 
                          'bg-green-100 text-green-600'
                        }`}>
                          {oc.status === 'aberta' ? 'Aberta' : oc.status === 'em_analise' ? 'Em Análise' : 'Resolvida'}
                        </span>
                      </div>
                      <p className="text-sm text-stone-600 mt-1">{oc.descricao}</p>
                    </div>
                    {oc.quantidade_afetada > 0 && (
                      <div className="text-right">
                        <p className="text-xs text-stone-500">Afetadas</p>
                        <p className="font-bold text-red-600">{oc.quantidade_afetada}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Operações */}
          <div>
            <h3 className="font-semibold mb-3">Progresso por Operação</h3>
            <div className="space-y-4">
              {progressoDetalhado.operacoes.map((op) => (
                <Card key={op.operacao_id} className={`p-4 border-l-4 ${getGrupoCor(op.grupo)}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="font-mono text-xs text-stone-400 mr-2">{op.codigo}</span>
                      <span className="font-medium">{op.nome}</span>
                      {op.grupo && <span className="ml-2 text-xs bg-stone-100 px-2 py-0.5 rounded">Grupagem {op.grupo}</span>}
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      op.status === 'concluida' ? 'bg-green-100 text-green-600' :
                      op.status === 'em_andamento' ? 'bg-amber-100 text-amber-600' :
                      op.status === 'em_progresso' ? 'bg-blue-100 text-blue-600' :
                      'bg-stone-100 text-stone-600'
                    }`}>
                      {op.status === 'concluida' ? 'Concluída' :
                       op.status === 'em_andamento' ? 'Em Andamento' :
                       op.status === 'em_progresso' ? 'Em Progresso' : 'Pendente'}
                    </span>
                  </div>

                  {/* Barra e Info de Tempo */}
                  <div className="grid grid-cols-4 gap-4 mb-3 text-center text-xs">
                    <div className="bg-stone-50 p-2 rounded">
                      <p className="text-stone-500">Produzido</p>
                      <p className="font-bold">{op.quantidade_produzida}/{op.quantidade_total}</p>
                    </div>
                    <div className="bg-blue-50 p-2 rounded">
                      <p className="text-blue-600">T. Estimado</p>
                      <p className="font-bold text-blue-700">{op.tempo_estimado_horas}h</p>
                    </div>
                    <div className="bg-amber-50 p-2 rounded">
                      <p className="text-amber-600">T. Gasto</p>
                      <p className="font-bold text-amber-700">{op.tempo_gasto_horas}h</p>
                    </div>
                    <div className="bg-green-50 p-2 rounded">
                      <p className="text-green-600">T. Restante</p>
                      <p className="font-bold text-green-700">{op.tempo_restante_horas}h</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 mb-3">
                    <div className="flex-1">
                      <div className="w-full h-3 bg-stone-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${
                            op.status === 'concluida' ? 'bg-green-500' :
                            op.status === 'em_andamento' ? 'bg-amber-500' : 'bg-primary'
                          }`}
                          style={{ width: `${op.progresso_percentual}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-sm font-bold w-16 text-right">{op.progresso_percentual}%</span>
                  </div>

                  {/* Produção por Operador */}
                  {op.operadores_producao && op.operadores_producao.length > 0 && (
                    <div className="mb-3 pt-3 border-t border-stone-100">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-stone-500 flex items-center gap-1">
                          <Users className="h-3 w-3" /> Produção por Operador:
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs text-blue-600 hover:text-blue-700"
                          onClick={() => onEditRegistos && onEditRegistos(op)}
                          data-testid={`ver-registos-btn-${op.operacao_id}`}
                        >
                          <Edit3 className="h-3 w-3 mr-1" />
                          Editar Registos
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {op.operadores_producao.map((opr) => (
                          <div key={opr.operador_id} className="flex items-center gap-2 bg-stone-50 p-2 rounded">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                              {opr.nome.charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium truncate">{opr.nome.split(' ')[0]}</p>
                              <p className="text-xs text-stone-500">{opr.quantidade} un. · {opr.tempo_horas}h</p>
                            </div>
                            <div className={`text-xs font-bold ${
                              opr.eficiencia >= 100 ? 'text-green-600' : 
                              opr.eficiencia >= 80 ? 'text-amber-600' : 'text-red-600'
                            }`}>
                              {opr.eficiencia}%
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Produção por Lote */}
                  {op.lotes_producao && op.lotes_producao.length > 0 && (
                    <div className="pt-3 border-t border-stone-100">
                      <p className="text-xs text-stone-500 mb-2 flex items-center gap-1">
                        <Layers className="h-3 w-3" /> Produção por Lote:
                      </p>
                      <div className="flex gap-2 flex-wrap">
                        {op.lotes_producao.map((lote) => (
                          <div key={lote.lote_id} className="bg-stone-50 px-3 py-2 rounded text-center">
                            <p className="text-xs text-stone-500">Lote {lote.numero_lote}</p>
                            <p className="font-bold text-sm">{lote.quantidade_produzida}/{lote.quantidade_lote}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>

          {/* Participantes */}
          {participantes && participantes.length > 0 && (
            <div>
              <h3 className="font-semibold mb-3">Todos os Operadores Participantes</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {participantes.map((p) => (
                  <Card key={p.operador_id} className="p-3 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="font-bold text-primary">{p.nome.charAt(0)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{p.nome}</p>
                      <p className="text-xs text-stone-500">{p.total_quantidade} un. · {p.total_tempo_horas}h</p>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
